import { useState, useCallback, useRef } from 'react'
import { Header } from './components/Header'
import { Footer } from './components/Footer'
import { ContentSection } from './components/ContentSection'
import { useI18n } from './i18n/index.tsx'
import * as pdfjs from 'pdfjs-dist'
import {
  Upload,
  FileText,
  Trash2,
  GripVertical,
  Merge,
  Download,
  AlertCircle,
  Plus,
  X,
  Check
} from 'lucide-react'

// Initialize pdf.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`

interface PDFFile {
  id: string
  file: File
  name: string
  size: number
  pageCount: number
  thumbnail: string | null
  error: string | null
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / 1024 / 1024).toFixed(1) + ' MB'
}

function App() {
  const { t } = useI18n()
  const [files, setFiles] = useState<PDFFile[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [progress, setProgress] = useState(0)
  const [mergedUrl, setMergedUrl] = useState('')
  const [mergedPages, setMergedPages] = useState(0)
  const [mergedSize, setMergedSize] = useState(0)
  const [error, setError] = useState('')
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const dragItemRef = useRef<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Extract page count and thumbnail from PDF
  const processPDF = useCallback(async (file: File): Promise<PDFFile> => {
    try {
      const arrayBuffer = await file.arrayBuffer()
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise
      const pageCount = pdf.numPages

      // Generate thumbnail from first page
      let thumbnail = null
      try {
        const page = await pdf.getPage(1)
        const scale = 0.3
        const viewport = page.getViewport({ scale })
        const canvas = document.createElement('canvas')
        canvas.width = viewport.width
        canvas.height = viewport.height
        const ctx = canvas.getContext('2d')
        if (ctx) {
          await page.render({ canvasContext: ctx, viewport }).promise
          thumbnail = canvas.toDataURL('image/png')
        }
      } catch {
        // Thumbnail generation failed, use icon instead
      }

      return {
        id: Math.random().toString(36).substring(2, 15),
        file,
        name: file.name,
        size: file.size,
        pageCount,
        thumbnail,
        error: null,
      }
    } catch {
      return {
        id: Math.random().toString(36).substring(2, 15),
        file,
        name: file.name,
        size: file.size,
        pageCount: 0,
        thumbnail: null,
        error: t('pdfMerge.invalidPdf') || 'Invalid or corrupted PDF file',
      }
    }
  }, [t])

  // Handle file upload
  const handleFiles = useCallback(async (newFiles: FileList | null) => {
    if (!newFiles) return
    setError('')
    if (mergedUrl) URL.revokeObjectURL(mergedUrl)
    setMergedUrl('')

    const pdfFiles = Array.from(newFiles).filter(f => f.type === 'application/pdf')
    if (pdfFiles.length === 0) {
      setError(t('pdfMerge.selectPdf') || 'Please select PDF files')
      return
    }

    const processed: PDFFile[] = []
    for (const file of pdfFiles) {
      const result = await processPDF(file)
      processed.push(result)
    }
    setFiles(prev => [...prev, ...processed])
  }, [processPDF, mergedUrl, t])

  // Remove file
  const removeFile = useCallback((id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
    if (mergedUrl) URL.revokeObjectURL(mergedUrl)
    setMergedUrl('')
  }, [mergedUrl])

  // Clear all
  const clearAll = useCallback(() => {
    setFiles([])
    if (mergedUrl) URL.revokeObjectURL(mergedUrl)
    setMergedUrl('')
    setError('')
  }, [mergedUrl])

  // Drag and drop reorder
  const onDragStart = useCallback((index: number) => {
    dragItemRef.current = index
  }, [])

  const onDragOver = useCallback((e: React.DragEvent, index: number) => {
    e.preventDefault()
    setDragOverIndex(index)
  }, [])

  const onDrop = useCallback((e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    setDragOverIndex(null)
    const dragIndex = dragItemRef.current
    if (dragIndex === null || dragIndex === dropIndex) return

    setFiles(prev => {
      const newFiles = [...prev]
      const [dragged] = newFiles.splice(dragIndex, 1)
      newFiles.splice(dropIndex, 0, dragged)
      return newFiles
    })
    dragItemRef.current = null
  }, [])

  const onDragEnd = useCallback(() => {
    setDragOverIndex(null)
    dragItemRef.current = null
  }, [])

  // Merge PDFs using pdf-lib
  const mergePDFs = useCallback(async () => {
    if (files.length < 2) {
      setError(t('pdfMerge.minFiles') || 'Please add at least 2 PDF files to merge')
      return
    }

    const validFiles = files.filter(f => !f.error)
    if (validFiles.length < 2) {
      setError(t('pdfMerge.minValid') || 'Please add at least 2 valid PDF files')
      return
    }

    setIsProcessing(true)
    setProgress(0)
    setError('')

    try {
      const { PDFDocument } = await import('pdf-lib')
      const mergedPdf = await PDFDocument.create()
      let totalPages = 0

      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i]
        const bytes = await file.file.arrayBuffer()
        const pdf = await PDFDocument.load(bytes)
        const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices())
        pages.forEach(page => mergedPdf.addPage(page))
        totalPages += pdf.getPageCount()
        setProgress(Math.round(((i + 1) / validFiles.length) * 100))
      }

      const mergedBytes = await mergedPdf.save()
      const blob = new Blob([new Uint8Array(mergedBytes)], { type: 'application/pdf' })
      const url = URL.createObjectURL(blob)

      setMergedUrl(url)
      setMergedPages(totalPages)
      setMergedSize(blob.size)
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setError((t('pdfMerge.mergeError') || 'Failed to merge PDFs: ') + msg)
    } finally {
      setIsProcessing(false)
      setProgress(0)
    }
  }, [files, t])

  // Download merged PDF
  const downloadMerged = useCallback(() => {
    if (!mergedUrl) return
    const a = document.createElement('a')
    a.href = mergedUrl
    a.download = `merged_${files.filter(f => !f.error).map(f => f.name.replace(/\.pdf$/i, '')).join('_')}.pdf`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
  }, [mergedUrl, files])

  const totalPages = files.filter(f => !f.error).reduce((sum, f) => sum + f.pageCount, 0)
  const validFileCount = files.filter(f => !f.error).length

  return (
    <div className="min-h-screen bg-background flex flex-col transition-colors duration-200">
      <Header />

      {/* Hero */}
      <div className="text-center py-6 px-4">
        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
          {t('pdfMerge.title') || 'PDF Merger'}
        </h1>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          {t('pdfMerge.subtitle') || 'Merge multiple PDF files into one. '}
          <span className="text-primary font-medium">{t('pdfMerge.privacy') || '100% client-side'}</span>, {t('pdfMerge.noUpload') || 'no upload needed'}.
        </p>
      </div>

      <main className="flex-1 w-full max-w-6xl mx-auto px-4 pb-8 space-y-4">
        {/* Error Banner */}
        {error && (
          <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Upload Area */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault()
            handleFiles(e.dataTransfer.files)
          }}
          className="bg-surface rounded-xl shadow-sm border border-border p-8 md:p-12 text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
            <Upload className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">
            {t('pdfMerge.uploadTitle') || 'Add PDF Files'}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('pdfMerge.uploadDesc') || 'Drag & drop or click to select. Reorder by dragging.'}
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              {t('pdfMerge.addFiles') || 'Add PDFs'}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              multiple
              className="hidden"
              onChange={e => handleFiles(e.target.files)}
            />
            {files.length > 0 && (
              <button
                onClick={clearAll}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-background border border-border text-foreground rounded-lg hover:bg-muted transition-colors text-sm font-medium"
              >
                <Trash2 className="w-4 h-4" />
                {t('actions.clear') || 'Clear All'}
              </button>
            )}
          </div>
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="bg-surface rounded-xl shadow-sm border border-border p-4 md:p-6 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">
                {t('pdfMerge.files') || 'Files'} ({files.length}) · {totalPages} {t('pdfMerge.pages') || 'pages'}
              </h3>
              <p className="text-xs text-muted-foreground">
                {t('pdfMerge.dragReorder') || 'Drag to reorder'}
              </p>
            </div>

            <div className="space-y-2">
              {files.map((file, index) => (
                <div
                  key={file.id}
                  draggable
                  onDragStart={() => onDragStart(index)}
                  onDragOver={(e) => onDragOver(e, index)}
                  onDrop={(e) => onDrop(e, index)}
                  onDragEnd={onDragEnd}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-move ${
                    dragOverIndex === index
                      ? 'border-brand-primary bg-brand-primary/5'
                      : file.error
                        ? 'border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-800'
                        : 'border-border bg-background hover:bg-muted/50'
                  }`}
                >
                  <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />

                  {file.thumbnail ? (
                    <img src={file.thumbnail} alt="" className="w-12 h-16 object-cover rounded border border-border shrink-0" />
                  ) : (
                    <div className="w-12 h-16 bg-muted rounded border border-border flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{file.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {file.error ? (
                        <span className="text-red-500">{file.error}</span>
                      ) : (
                        <>
                          {formatSize(file.size)} · {file.pageCount} {t('pdfMerge.pages') || 'pages'}
                        </>
                      )}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground font-mono">#{index + 1}</span>
                    <button
                      onClick={() => removeFile(file.id)}
                      className="p-1.5 text-muted-foreground hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 rounded transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Merge Button */}
            <div className="pt-2">
              {isProcessing ? (
                <div className="space-y-2">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-primary rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    {t('pdfMerge.merging') || 'Merging...'} {progress}%
                  </p>
                </div>
              ) : mergedUrl ? (
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800 rounded-lg p-3 flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500 shrink-0" />
                    <div className="text-sm">
                      <p className="text-green-700 dark:text-green-300 font-medium">
                        {t('pdfMerge.merged') || 'Merged successfully'}
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-400">
                        {mergedPages} {t('pdfMerge.pages') || 'pages'} · {formatSize(mergedSize)}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={downloadMerged}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium shrink-0"
                  >
                    <Download className="w-4 h-4" />
                    {t('actions.download') || 'Download'}
                  </button>
                </div>
              ) : (
                <button
                  onClick={mergePDFs}
                  disabled={validFileCount < 2}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-brand-primary text-white rounded-lg hover:opacity-90 transition-opacity text-sm font-medium disabled:opacity-50"
                >
                  <Merge className="w-4 h-4" />
                  {t('pdfMerge.merge') || 'Merge PDFs'}
                  {validFileCount >= 2 && (
                    <span className="text-xs opacity-70">
                      ({validFileCount} {t('pdfMerge.files') || 'files'}, {totalPages} {t('pdfMerge.pages') || 'pages'})
                    </span>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Privacy Note */}
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            {t('security.note')}
          </p>
        </div>
      </main>

      <ContentSection />


      <Footer />
    </div>
  )
}

export default App
